<!--
  AcceptMenteeDialog.vue (Vue 2)
  -------------------------------------------------------------
  Centered modal used to accept a pending mentee signup. The manager assigns a
  mentor (from the active mentors at this account) and confirms a hire date in
  MM/YY format, then confirms. Ported from the Accept dialog in the React
  ManagerAdminPage.

  Props:
    - open (Boolean) ........ controlled visibility (use with .sync)
    - signup (Object|null) .. the pending signup being accepted
    - mentors (Array) ....... active mentors [{ id, name }]

  Events:
    - update:open(false) .... emitted on overlay click, Cancel, X, or Esc
    - confirm({ mentorId, mentorName, hireDate }) .. emitted on Accept Mentee

  Dependency: lucide-vue
-->
<template>
  <transition name="mm-fade">
    <div v-if="open" class="mm-modal-root">
      <div class="mm-modal-overlay" @click="close"></div>
      <div class="mm-modal" role="dialog" aria-modal="true">
        <button type="button" class="mm-modal-close" aria-label="Close" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x h-4 w-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/ui/dialog.tsx:46:8" data-component-name="X"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
        </button>

        <header class="mm-modal-head">
          <h2 class="mm-modal-title">
            Accept {{ signup ? `${signup.first_name} ${signup.last_name}` : "mentee" }}
          </h2>
          <p class="mm-modal-desc">
            Assign a mentor and confirm the hire date to add this mentee to your account.
          </p>
        </header>

        <div class="mm-modal-body">
          <div class="mm-field">
            <label class="mm-label" for="mm-accept-mentor">Assign Mentor</label>
            <select id="mm-accept-mentor" v-model="mentorId" class="mm-select">
              <option value="" disabled>
                {{ mentors.length ? "Select a mentor" : "No mentors available" }}
              </option>
              <option v-for="m in mentors" :key="m.id" :value="String(m.id)">
                {{ m.groupName }}
              </option>
            </select>
            <p v-if="mentors.length === 0" class="mm-hint">
              No active mentors at this account yet. Add a mentor before accepting.
            </p>
          </div>

          <div class="mm-field">
            <label class="mm-label" for="mm-accept-hire">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/ManagerAdminPage.tsx:569:16" data-component-name="Calendar"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg> Hire Date (MM/YY)
            </label>
            <input
              id="mm-accept-hire"
              class="mm-input mm-mono mm-input--sm"
              inputmode="numeric"
              placeholder="MM/YY"
              :value="hireDate"
              maxlength="5"
              @input="onHireInput"
            />
            <p v-if="hireDate.length > 0 && !hireDateValid" class="mm-error">
              Enter a valid month and year (MM/YY). Future dates are not allowed.
            </p>
          </div>
        </div>

        <footer class="mm-modal-foot">
          <button type="button" class="mm-btn mm-btn--outline" @click="close">Cancel</button>
          <button
            type="button"
            class="mm-btn mm-btn--green"
            :disabled="(!canConfirm || (disable_add_mentee == true))"
            @click="confirm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-plus w-4 h-4 mr-1.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/pages/ManagerAdminPage.tsx:592:14" data-component-name="UserPlus"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" x2="19" y1="8" y2="14"></line><line x1="22" x2="16" y1="11" y2="11"></line></svg>
            <span class="mm-btn-text">Accept Mentee</span>
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { notify } from "../../toastStore";

export default {

  name: "AcceptMenteeDialog",
  props: {
    open: { type: Boolean, default: false },
    signup: { type: Object, default: null },
    mentors: { type: Array, default: () => [] },
  },
  data() {
    return {
      mentorId: "",
      hireDate: "",
      hireDateValue: '',
      successMessage: '',
      disable_add_mentee: false,
    };
  },
  computed: {
    hireDateValid() {
      const value = this.hireDate.trim();

      // Check MM/YY format
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) {
        return false;
      }

      const [month, year] = value.split("/");

      const hireDate = moment({
        year: 2000 + Number(year),
        month: Number(month) - 1, // moment months are 0-based
        day: 1
      });

      return hireDate.isSameOrBefore(moment(), "month");

      
    },
    canConfirm() {
      return !!this.mentorId && this.hireDateValid;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        // reset on each open
        this.mentorId = "";
        this.hireDate = "";
        this.hireDateValue = "";
        document.addEventListener("keydown", this.onKeydown);
      } else {
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    hide() { 
      this.$emit('close');
    },
    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },
    onHireInput(e) {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
      this.hireDate = digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
    },
    close() {
      this.$emit("update:open", false);
    },
    confirm() {
      if (!this.canConfirm) return;
      const mentor = this.mentors.find((m) => String(m.id) === this.mentorId);
      if (!mentor) return;
      this.updateMentee('Approved');  
    },

    onSubmit(status) {
      this.$validator.validateAll().then((result) => {
        if(!result) {
          return;
        }
        this.updateMentee(status);
      })
      .catch(() => {

      });
    },
    onDenySubmit(status) {
      this.updateMentee(status);
    },
    updateMentee(status) {
      this.disable_add_mentee = true;
      if (!this.hireDate) {
        this.hireDateValue = todayMoment.format('YYYY-MM-01');
      }
      else {
        const match = this.hireDate.trim().match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
        const month = match[1];
        const year = `20${match[2]}`;
        this.hireDateValue = `${year}-${month}-01`;
      }
      axios('/account-invitation-update-mentee',
      {
        method: 'POST',
        data: {
          "hire_date": this.hireDateValue,
          "mentor": this.mentorId,
          "account_id": this.$store.state.user_accountid,
          "menteeId": this.signup.id,
          "status": 'Approved',
          "menteeInviteId": this.signup.menteeInviteId,
        }
      })
      .then(response => {
          this.disable_add_mentee = false;
          const mentor = this.mentors.find((m) => String(m.id) === this.mentorId);
          notify({
            title: "Mentee accepted",
            description: `${this.signup.first_name} ${this.signup.last_name} assigned to ${mentor.groupName} · hired ${this.hireDateValue}.`,
          });
          
          this.hide();
      })
      .catch(error => {
          this.disable_add_mentee = false;
      });
    },

  },
};
</script>

<style scoped>
.mm-modal-root {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.mm-modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}
.mm-modal {
  position: relative;
  width: 100%;
  max-width: 28rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  padding: 24px;
}
.mm-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #6b7785;
  border-radius: 6px;
  cursor: pointer;
}
.mm-modal-close:hover {
  background: #f3f5f6;
}
.mm-modal-head {
  margin-bottom: 16px;
  padding-right: 24px;
}
.mm-modal-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #002f42;
}
.mm-modal-desc {
  margin: 0;
  font-size: 13px;
  color: #6b7785;
}
.mm-modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}
.mm-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mm-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2a30;
  margin-bottom: 0 !important;
}
.mm-select,
.mm-input {
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2a30;
  background: #fff;
  outline: none;
}
.mm-select {
  width: 100%;
}
.mm-input--sm {
  width: 8rem;
}
.mm-select:focus,
.mm-input:focus {
  border-color: #00acee;
  box-shadow: 0 0 0 3px rgba(0, 172, 238, 0.15);
}
.mm-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.mm-hint {
  margin: 0;
  font-size: 11px;
  color: #6b7785;
}
.mm-error {
  margin: 0;
  font-size: 11px;
  color: #ee4200;
}
.mm-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
.mm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.mm-btn-text {
  white-space: nowrap;
}
.mm-btn--outline {
  background: #fff;
  border-color: #d6dde1;
  color: #1f2a30;
}
.mm-btn--outline:hover {
  background: #f3f5f6;
}
.mm-btn--green {
  background: #009118;
  color: #fff;
}
.mm-btn--green:hover {
  background: #007814;
}
.mm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.mm-fade-enter-active,
.mm-fade-leave-active {
  transition: opacity 0.18s ease;
}
.mm-fade-enter,
.mm-fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/manager/AcceptMenteeDialog.vue